import React from "react";
import ReactApexChart from "react-apexcharts";
import Utils from "Utils/utils";
import { getNiceMax } from "../utils/chartDataUtils";
import MemberBreakdownList from "./MemberBreakdownList";

const toMemberTotals = rows => {
  if (!rows?.length) return [];
  const byMember = {};
  rows.forEach(r => {
    if (!byMember[r.memberId]) {
      byMember[r.memberId] = { memberId: r.memberId, name: r.name, count: 0 };
    }
    byMember[r.memberId].count += r.count || 0;
  });
  return Object.values(byMember);
};

const COLOR = "#3953fb";
// Context grey for the bars that are not the point. Emphasis colouring — one
// accented bar among grey ones — is what makes a chart say something; six bars
// in one hue say "here is some data" and nothing more.
const MUTED = "#c7cbd6";

const humanizeStage = code =>
  (code || "")
    .split("_")
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
const stageDisplay = s => s.stageLabel || humanizeStage(s.stage);

/**
 * `showAxes` reveals the category and value labels. The dashboard hides them —
 * the card is small and the legend lives in WidgetCard — but a chart in the
 * chat has no surrounding card to name its bars, so an unlabelled chart there
 * is just coloured rectangles. Defaults to the dashboard's behaviour.
 */
const ContactStagesWidget = ({
  contactStages = [],
  memberBreakdown,
  breakdownLoading,
  visualization,
  showAxes = false,
  // `highlightStage` accents ONE bar and greys the rest. `onSelect` makes a bar
  // clickable, turning the chart into navigation ("show me those 534 bounces")
  // rather than a dead end. Both default off, so the dashboard is unchanged.
  highlightStage = null,
  onSelect = null
}) => {
  if (!contactStages.length) {
    return <p className="contactStagesEmpty">No contact stage data</p>;
  }

  const labels = contactStages.map(stageDisplay);
  const counts = contactStages.map(s => s.count);
  const { max: niceMax, tickAmount } = getNiceMax(Math.max(...counts, 1));

  const isKpi = visualization === "KPI";
  const isLine = visualization === "LINE";

  const chartBody = isKpi ? (
    <div className="widgetCardNumericSummary">
      {contactStages.map(s => (
        <div
          key={s.stage}
          className={`widgetCardNumericSummaryRow${
            highlightStage && s.stage === highlightStage ? " is-emphasis" : ""
          }${onSelect ? " is-clickable" : ""}`}
          onClick={onSelect ? () => onSelect(s) : undefined}
        >
          <span className="widgetCardNumericSummaryLabel">{stageDisplay(s)}</span>
          <span className="widgetCardNumericSummaryValue">{Utils.formatNumberToStringInUSFormat(s.count)}</span>
        </div>
      ))}
    </div>
  ) : (
    (() => {
      const options = {
        chart: {
          type: isLine ? "area" : "bar",
          toolbar: { show: false },
          animations: { enabled: false },
          selection: { enabled: false },
          zoom: { enabled: false },
          events: onSelect
            ? {
                dataPointSelection: (event, ctx, cfg) => {
                  const stage = contactStages[cfg.dataPointIndex];
                  if (stage) onSelect(stage);
                }
              }
            : undefined
        },
        ...(isLine
          ? {
              stroke: { show: true, curve: "smooth", width: 2, colors: [COLOR] },
              markers: { size: 4, colors: [COLOR], strokeColors: "#fff", strokeWidth: 2, hover: { size: 5 } },
              fill: { type: "solid", opacity: 0.2 }
            }
          : {
              plotOptions: { bar: { borderRadius: 3, columnWidth: "90%", distributed: true } }
            }),
        // Per-bar colours require `distributed`, which the bar path already sets.
        colors: highlightStage
          ? contactStages.map(stage => (stage.stage === highlightStage ? COLOR : MUTED))
          : [COLOR],
        dataLabels: { enabled: false },
        legend: { show: false },
        xaxis: {
          categories: labels,
          labels: showAxes
            ? { show: true, rotate: -35, trim: true, hideOverlappingLabels: true, style: { fontSize: "11px" } }
            : { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false }
        },
        yaxis: {
          min: 0,
          max: niceMax,
          tickAmount,
          labels: showAxes ? { show: true, style: { fontSize: "11px" } } : { show: false }
        },
        grid: {
          borderColor: "#e8ebee",
          strokeDashArray: 3,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } }
        },
        tooltip: {
          x: { show: true },
          y: { formatter: val => `${val} contacts` }
        }
      };
      const series = [{ name: "Contacts", data: counts }];
      return (
        <ReactApexChart key={isLine ? "area" : "bar"} options={options} series={series} type={isLine ? "area" : "bar"} height={140} width="100%" />
      );
    })()
  );

  return (
    <div className="contactStagesWrap">
      <p className="contactStagesSnapshotLabel">Live snapshot</p>
      <MemberBreakdownList rows={toMemberTotals(memberBreakdown)} loading={breakdownLoading} />
      {chartBody}
    </div>
  );
};

export default ContactStagesWidget;
